/**
 * tensorfield.ts — the era'd TENSOR DIRECTION FIELD + per-settlement CULTURE
 * vectors (W2d-2 Task 2). PURE + UNWIRED: nothing in the generation pipeline
 * calls this module yet, so all four worldgen digests HOLD unchanged. Task 3's
 * secondary-street grower consumes it to steer segments.
 *
 * WHAT IT COMPUTES. Given a settlement (seed, tier, radius, culture, terrain) and
 * a chart point (u,v) in an era band, {@link tensorDirAt} returns the local street
 * direction as the MAJOR eigenvector of a blended symmetric tensor field. Three
 * component fields are blended per the spec's tier×era weight table:
 *
 *   RADIAL  — organised around the settlement seed. Streets RING the seed (the
 *             MAJOR direction is tangent to circles centred on the seed); the
 *             perpendicular MINOR direction is the SPOKE (toward/away from the
 *             seed). This module returns the major (ring); the grower (T3) may
 *             follow the major OR the ±90° minor — see {@link radialRingDir} /
 *             {@link radialSpokeDir}. Classic tensor-field streets (Chen et al.
 *             2008) use exactly this eigenvector pair.
 *   GRID    — a settlement-stable hashed orientation θ0 (a straight-street grid).
 *             As a symmetric field its axis is {θ0, θ0+90°}; the field's major
 *             direction is θ0. At GROWTH time the grower snaps a moving front to
 *             whichever of {θ0, θ0+90°} is nearest its current heading — see
 *             {@link gridSnapDir} (a T3 helper; tensorDirAt itself takes no
 *             heading, so it contributes the θ0 axis to the blend).
 *   TERRAIN — the CONTOUR (isoline) direction, read from the SAME full-surface
 *             {@link RouteTerrain} field the router's slope/contour uses (carry #5:
 *             ONE field serves both slope AND contour — this module reads slope via
 *             {@link slopeDegAt} and contour via `terrain.isoDir`, both off the same
 *             accessor). Streets follow contours instead of charging uphill.
 *
 * TENSOR BLEND (why not a plain vector average). RADIAL/GRID/TERRAIN are AXIAL
 * fields — a "direction" and its 180° reverse are the same street. Averaging raw
 * unit vectors is wrong (θ and θ+180 would cancel). We blend in the DOUBLE-ANGLE
 * (symmetric-tensor) encoding: a component at axis angle φ with weight w adds
 * w·(cos2φ, sin2φ); the blended major axis is ½·atan2(Σw·sin2φ, Σw·cos2φ). Because
 * we only need cos2φ / sin2φ, and each component already gives a unit axis vector
 * (ax,ay), we use the identities cos2φ = ax²−ay², sin2φ = 2·ax·ay and never call a
 * trig on the components. The final decode is one atan2 + cos/sin (all pmath).
 * atan2 is scale-invariant, so the blended DIRECTION is unaffected by the overall
 * weight scale — weight normalisation matters for {@link tensorWeights} (the
 * documented per-district split) and for the RELATIVE influence of the components,
 * not for the numeric stability of the decode.
 *
 * WEIGHTS (spec §"Tensor field weights", BINDING verbatim — {@link TENSOR_WEIGHTS};
 * the rows sum to 1 already). Two documented modulations:
 *   - CULTURE gridW: the grid weight is lerped 50% toward the culture's `gridW`
 *     knob, then the triple is renormalised to sum 1 (so a hieratic culture,
 *     gridW 0.8, pulls the blend toward the grid axis; a grown culture, gridW 0.15,
 *     toward radial/terrain). See {@link CULTURE_BLEND}.
 *   - SLOPE clamp (spec §"Tensor field weights": "clamp terrain ≥0.5 on slopes
 *     >15°"): where slope > {@link TERRAIN_SLOPE_CLAMP_DEG}, the terrain weight is
 *     forced to ≥ {@link TERRAIN_CLAMP_MIN} and the remaining weight is split
 *     between radial/grid in their existing ratio (still sums to 1).
 *
 * HEADING NOISE (spec §"Heading noise", BINDING — {@link HEADING_NOISE_DEG}). Each
 * grown segment deviates from the field by a per-segment uniform draw in ±bound.
 * The bound is the tier×era table value lerped 50% toward the culture's `noiseDeg`
 * knob (the SAME 50% culture blend as grid weight), i.e. ½·(table + noiseDeg) — see
 * {@link headingNoiseBoundDeg}. Hash-per-segment (registered salt 0x4e15).
 *
 * DETERMINISM / PORTABILITY. Every roll is a pure hashInts stream (salts registered
 * in hash.ts: 0xc0de culture, 0x7e50 grid θ0, 0x4e15 heading noise). All float math
 * routes through pmath; no `**`; no engine-approximated intrinsic (the pmath-ban
 * gate covers this file). Zero imports beyond shared internals.
 *
 * TIER INDEXING. Tiers are the INTERNAL indices (tierRegistry: village=2, town=3,
 * city=4, metropolis=5). Sub-village tiers (waystation/hamlet) have no tensor field
 * in the spec (a hamlet is one through-road with ±25° noise, field off); the grower
 * never calls this below village, but to stay NaN-free the tables clamp any tier
 * into [village, metropolis] and any era band into that tier's defined range
 * (rows collapse to the outermost defined band for out-of-range bands).
 */
import * as pmath from "../pmath.js";
import { hashInts, h01 } from "./hash.js";
import { TIER_VILLAGE, TIER_METROPOLIS } from "./tierRegistry.js";
import type { RouteTerrain } from "./router.js";

const RAD2DEG = 180 / Math.PI; // Math.PI is not a banned intrinsic; portable constant.
const HALF_PI = Math.PI / 2;

// Registered salt streams (see hash.ts salt registry — UNWIRED this phase).
const SALT_CULTURE = 0xc0de; // per-settlement culture preset roll
const SALT_GRID = 0x7e50; //    per-settlement grid orientation θ0
const SALT_NOISE = 0x4e15; //   per-segment heading noise

// ── culture vectors ────────────────────────────────────────────────────────────

/**
 * The five knobs that most change an architectural culture's visual identity
 * (spec §"Culture vector"). `blockAreaScale` and `lotSkew` are STORED for W2d-3
 * (block subdivision / lot skew) and are not consumed by the tensor field itself.
 */
export interface CultureVector {
  /** Minimum junction angle (degrees) the grower will accept. */
  minAngleDeg: number;
  /** Heading-noise magnitude knob (degrees) — blended into the noise bound. */
  noiseDeg: number;
  /** Grid-axis preference in [0,1] — blended into the grid weight. */
  gridW: number;
  /** Block-area multiplier (W2d-3; stored only). */
  blockAreaScale: number;
  /** Irregular-lot cut-point skew in [0,1] (W2d-3; stored only). */
  lotSkew: number;
}

/**
 * The preset spectrum (spec §"Culture vector"): the two named poles plus two
 * blends. hieratic = {45°, ±3°, grid 0.8, small blocks, low skew}; grown =
 * {25°, ±20°, radial-dominant (grid 0.15), variable blocks, high skew}. blendA/B
 * are the linear interpolations at t = 1/3 and 2/3 (rounded to 4 dp), so the four
 * presets read as a hieratic→grown gradient. Index order is the roll order.
 */
export const CULTURE_PRESETS: readonly CultureVector[] = [
  { minAngleDeg: 45, noiseDeg: 3, gridW: 0.8, blockAreaScale: 0.7, lotSkew: 0.08 }, // 0 hieratic
  { minAngleDeg: 38.3333, noiseDeg: 8.6667, gridW: 0.5833, blockAreaScale: 0.9, lotSkew: 0.1533 }, // 1 blendA (t=1/3)
  { minAngleDeg: 31.6667, noiseDeg: 14.3333, gridW: 0.3667, blockAreaScale: 1.1, lotSkew: 0.2267 }, // 2 blendB (t=2/3)
  { minAngleDeg: 25, noiseDeg: 20, gridW: 0.15, blockAreaScale: 1.3, lotSkew: 0.3 }, // 3 grown
];

export const CULTURE_PRESET_NAMES: readonly string[] = ["hieratic", "blendA", "blendB", "grown"];

/**
 * Hash-roll a settlement's culture vector from {@link CULTURE_PRESETS} (uniform
 * over the four presets). Deterministic per (planet, cell, seed). Returns a COPY
 * so callers cannot mutate the shared preset object.
 */
export function cultureFor(planetHash: number, cellKey: number, k: number): CultureVector {
  const idx = Math.min(CULTURE_PRESETS.length - 1, Math.floor(h01(hashInts(planetHash, SALT_CULTURE, cellKey, k)) * CULTURE_PRESETS.length));
  const p = CULTURE_PRESETS[idx];
  return { minAngleDeg: p.minAngleDeg, noiseDeg: p.noiseDeg, gridW: p.gridW, blockAreaScale: p.blockAreaScale, lotSkew: p.lotSkew };
}

/**
 * The settlement-stable grid orientation θ0 (radians) in [0, π/2). Grid axes are
 * 90°-symmetric, so a quarter-turn spans every distinct grid. Deterministic per
 * (planet, cell, seed); rolled once and carried in {@link TensorCtx}.
 */
export function gridOrientationFor(planetHash: number, cellKey: number, k: number): number {
  return h01(hashInts(planetHash, SALT_GRID, cellKey, k)) * HALF_PI;
}

// ── weight tables (spec, BINDING verbatim; oldest era → newest) ────────────────

/**
 * Tensor weights [radial, grid, terrain] indexed [tier 0..5][eraBand]. Rows sum to
 * 1. Only village(2)/town(3)/city(4)/metropolis(5) are populated (the spec has no
 * sub-village field); waystation(0)/hamlet(1) reuse the village row defensively.
 * Village has 1 band, town 2, city/metro 3 — {@link rowFor} clamps out-of-range
 * bands to the outermost defined band.
 */
export const TENSOR_WEIGHTS: readonly (readonly (readonly [number, number, number])[])[] = [
  [[0.2, 0.3, 0.5]], //                                                     0 waystation → village row
  [[0.2, 0.3, 0.5]], //                                                     1 hamlet     → village row
  [[0.2, 0.3, 0.5]], //                                                     2 village
  [[0.5, 0.2, 0.3], [0.2, 0.5, 0.3]], //                                    3 town
  [[0.6, 0.1, 0.3], [0.3, 0.5, 0.2], [0.1, 0.7, 0.2]], //                   4 city
  [[0.7, 0.1, 0.2], [0.3, 0.5, 0.2], [0.05, 0.85, 0.1]], //                 5 metropolis
];

/** Heading-noise bound (± degrees) indexed [tier 0..5][eraBand] (spec table). */
export const HEADING_NOISE_DEG: readonly (readonly number[])[] = [
  [20], //             0 waystation → village row
  [20], //             1 hamlet     → village row
  [20], //             2 village
  [18, 10], //         3 town
  [15, 8, 4], //       4 city
  [15, 7, 3], //       5 metropolis
];

/** Slope (degrees) above which the terrain weight is clamped up (spec: 15°). */
export const TERRAIN_SLOPE_CLAMP_DEG = 15;
/** The clamped-up minimum terrain weight on steep ground (spec: ≥0.5). */
export const TERRAIN_CLAMP_MIN = 0.5;
/** Fraction of the way the grid weight / noise bound is lerped toward the culture knob. */
export const CULTURE_BLEND = 0.5;

/** Finite-difference half-step (m) for the slope gradient. */
const SLOPE_STEP_M = 5;
/** Distance (m) below which the radial field is undefined (at the seed). */
const SEED_EPS_M = 1e-6;
/** Tensor-magnitude floor below which the blend is treated as degenerate. */
const TENSOR_EPS = 1e-12;

function clampTier(tier: number): number {
  if (tier < TIER_VILLAGE) return TIER_VILLAGE;
  if (tier > TIER_METROPOLIS) return TIER_METROPOLIS;
  return tier | 0;
}

/** The weight row for (tier, eraBand), clamping the band into the tier's range. */
function rowFor(tier: number, eraBand: number): readonly [number, number, number] {
  const rows = TENSOR_WEIGHTS[clampTier(tier)];
  let b = eraBand | 0;
  if (b < 0) b = 0;
  if (b > rows.length - 1) b = rows.length - 1;
  return rows[b];
}

/**
 * The blended, normalised weights {radial, grid, terrain} for a district — the
 * spec row, modulated by the culture grid preference (lerp 50% toward `cultureGridW`
 * then renormalise) and by the >15° slope terrain clamp (terrain ≥ 0.5, remainder
 * split radial:grid by ratio). Always sums to 1. Pure — the direct oracle target.
 */
export function tensorWeights(tier: number, eraBand: number, cultureGridW: number, slopeDeg: number): { radial: number; grid: number; terrain: number } {
  const row = rowFor(tier, eraBand);
  let r = row[0];
  let g = row[1] + CULTURE_BLEND * (cultureGridW - row[1]);
  let t = row[2];
  if (g < 0) g = 0;
  let s = r + g + t;
  if (s > TENSOR_EPS) { r /= s; g /= s; t /= s; }

  if (slopeDeg > TERRAIN_SLOPE_CLAMP_DEG && t < TERRAIN_CLAMP_MIN) {
    const rem = 1 - TERRAIN_CLAMP_MIN; // weight left for radial+grid after the clamp
    const rg = r + g;
    if (rg > TENSOR_EPS) { r = (r / rg) * rem; g = (g / rg) * rem; } else { r = rem * 0.5; g = rem * 0.5; }
    t = TERRAIN_CLAMP_MIN;
  }
  return { radial: r, grid: g, terrain: t };
}

// ── component directions (exposed; trivial) ────────────────────────────────────

/**
 * The RADIAL major direction at (u,v): the RING — a unit vector tangent to the
 * circle through (u,v) centred on the seed (perpendicular to the radius). Returns
 * the grid-less fallback (1,0) exactly at the seed where the ring is undefined.
 */
export function radialRingDir(seedU: number, seedV: number, u: number, v: number): { du: number; dv: number } {
  const ru = u - seedU;
  const rv = v - seedV;
  const len = pmath.hypot(ru, rv);
  if (len < SEED_EPS_M) return { du: 1, dv: 0 };
  return { du: -rv / len, dv: ru / len }; // perpendicular to the radius
}

/**
 * The RADIAL minor direction at (u,v): the SPOKE — a unit vector pointing toward
 * the seed (the ±90° partner of {@link radialRingDir}). Fallback (1,0) at the seed.
 */
export function radialSpokeDir(seedU: number, seedV: number, u: number, v: number): { du: number; dv: number } {
  const ru = u - seedU;
  const rv = v - seedV;
  const len = pmath.hypot(ru, rv);
  if (len < SEED_EPS_M) return { du: 1, dv: 0 };
  return { du: -ru / len, dv: -rv / len }; // toward the seed
}

/** The GRID major axis direction (cos θ0, sin θ0). */
export function gridAxisDir(theta0: number): { du: number; dv: number } {
  return { du: pmath.cos(theta0), dv: pmath.sin(theta0) };
}

/**
 * Snap a moving front's heading to the grid: return whichever of the grid axis
 * {θ0, θ0+90°} (in the heading's own half-plane) is nearest the heading (du,dv).
 * The GROWTH-time grid rule (T3) — tensorDirAt itself takes no heading. The result
 * is a unit vector on the same side as the heading (dot ≥ 0).
 */
export function gridSnapDir(theta0: number, du: number, dv: number): { du: number; dv: number } {
  const ax = pmath.cos(theta0);
  const ay = pmath.sin(theta0);
  const bx = -ay; // θ0 + 90°
  const by = ax;
  const da = du * ax + dv * ay;
  const db = du * bx + dv * by;
  let sx: number;
  let sy: number;
  if ((da < 0 ? -da : da) >= (db < 0 ? -db : db)) { sx = ax; sy = ay; if (da < 0) { sx = -sx; sy = -sy; } }
  else { sx = bx; sy = by; if (db < 0) { sx = -sx; sy = -sy; } }
  return { du: sx, dv: sy };
}

// ── slope + the blended field ──────────────────────────────────────────────────

/**
 * Local slope in degrees at chart (u,v) from a central finite difference of the
 * terrain height field (the SAME accessor the router uses). slope = atan(|∇h|).
 */
export function slopeDegAt(terrain: RouteTerrain, u: number, v: number): number {
  const h = SLOPE_STEP_M;
  const gu = (terrain.heightM(u + h, v) - terrain.heightM(u - h, v)) / (2 * h);
  const gv = (terrain.heightM(u, v + h) - terrain.heightM(u, v - h)) / (2 * h);
  const grad = pmath.hypot(gu, gv);
  return pmath.atan2(grad, 1) * RAD2DEG;
}

/**
 * The per-settlement context the tensor field reads. Assembled by Task 3 from the
 * settlement's identity (planet/cell/seed) + its rolled culture ({@link cultureFor})
 * and grid orientation ({@link gridOrientationFor}) + the shared terrain field.
 */
export interface TensorCtx {
  /** hashSeed(planet.seed). */
  planetHash: number;
  /** packCell(regionId) — the settlement's region cell key. */
  cellKey: number;
  /** Seed index within the cell (distinguishes co-located settlements' streams). */
  k: number;
  /** Settlement seed in region-chart metres. */
  seedU: number;
  seedV: number;
  /** Settlement radius (m) — era banding scale (carried for T3; not read here). */
  radiusM: number;
  /** Internal tier index (tierRegistry: village=2 … metropolis=5). */
  tier: number;
  /** Settlement-stable grid orientation θ0 (radians), from {@link gridOrientationFor}. */
  gridTheta0: number;
  /** The settlement's culture vector, from {@link cultureFor}. */
  culture: CultureVector;
  /** The SAME full-surface terrain field the router's slope/contour uses. */
  terrain: RouteTerrain;
}

/**
 * The blended street direction at chart (u,v) in era band `eraBand` (0=old core,
 * 1=mid, 2=new/outer): the MAJOR eigenvector of the weighted RADIAL⊕GRID⊕TERRAIN
 * symmetric tensor, as a UNIT vector {du,dv}. See the module header for the ring /
 * grid / contour semantics and the double-angle blend. NaN-free on every input:
 *   - at the seed the radial component is dropped (undefined ring);
 *   - on flat ground the terrain component is dropped (isoDir returns false);
 *   - if the tensor fully degenerates (all present components cancel), the grid
 *     axis (cos θ0, sin θ0) is the documented fallback.
 */
export function tensorDirAt(ctx: TensorCtx, u: number, v: number, eraBand: 0 | 1 | 2): { du: number; dv: number } {
  const slopeDeg = slopeDegAt(ctx.terrain, u, v);
  const w = tensorWeights(ctx.tier, eraBand, ctx.culture.gridW, slopeDeg);

  let sc = 0; // Σ w·cos2φ
  let ss = 0; // Σ w·sin2φ

  // RADIAL — ring (major) axis, dropped at the seed.
  const ru = u - ctx.seedU;
  const rv = v - ctx.seedV;
  const rlen = pmath.hypot(ru, rv);
  if (rlen > SEED_EPS_M) {
    const tx = -rv / rlen; // ring tangent (perpendicular to radius)
    const ty = ru / rlen;
    sc += w.radial * (tx * tx - ty * ty);
    ss += w.radial * (2 * tx * ty);
  }

  // GRID — the θ0 axis (always present).
  const gx = pmath.cos(ctx.gridTheta0);
  const gy = pmath.sin(ctx.gridTheta0);
  sc += w.grid * (gx * gx - gy * gy);
  ss += w.grid * (2 * gx * gy);

  // TERRAIN — contour axis, dropped on flat ground.
  const iso: [number, number] = [0, 0];
  if (ctx.terrain.isoDir(u, v, iso)) {
    const iu = iso[0];
    const iv = iso[1];
    sc += w.terrain * (iu * iu - iv * iv);
    ss += w.terrain * (2 * iu * iv);
  }

  const mag = pmath.hypot(sc, ss);
  if (mag < TENSOR_EPS) return { du: gx, dv: gy }; // degenerate: grid-axis fallback
  const ang = 0.5 * pmath.atan2(ss, sc);
  return { du: pmath.cos(ang), dv: pmath.sin(ang) };
}

// ── heading noise ──────────────────────────────────────────────────────────────

/**
 * The heading-noise bound (± degrees) for (tier, era): the spec table value lerped
 * 50% toward the culture's `noiseDeg` knob, i.e. ½·(table + culture.noiseDeg).
 */
export function headingNoiseBoundDeg(ctx: TensorCtx, eraBand: 0 | 1 | 2): number {
  const rows = HEADING_NOISE_DEG[clampTier(ctx.tier)];
  let b = eraBand | 0;
  if (b < 0) b = 0;
  if (b > rows.length - 1) b = rows.length - 1;
  const table = rows[b];
  return table + CULTURE_BLEND * (ctx.culture.noiseDeg - table);
}

/**
 * A deterministic per-segment heading deviation (degrees), uniform in ±bound where
 * bound = {@link headingNoiseBoundDeg}. `segIndex` identifies the segment within the
 * settlement; `saltStream` fans out independent streams (per front / retry) without
 * a new salt. Hash-per-segment (salt 0x4e15).
 */
export function headingNoiseDeg(ctx: TensorCtx, eraBand: 0 | 1 | 2, segIndex: number, saltStream: number): number {
  const bound = headingNoiseBoundDeg(ctx, eraBand);
  const r = h01(hashInts(ctx.planetHash, SALT_NOISE, ctx.cellKey, ctx.k, segIndex, saltStream));
  return (r * 2 - 1) * bound;
}
